import request from "@/api";
import type {
    FindContractSpecialsItem,
    SpecialZoneContractCreateParams,
    SpecialZoneContractPagingParams,
    SpecialZoneContractPagingRow,
    SpecialZoneContractPriceAdjustLog,
    SpecialZoneContractUpdateParams
} from "@zhihaoscm/entity";
import type { ZoneContractPageResult, ZoneContractReservationResult } from "./zoneContractReactTypes";

/** React 页面 API 适配层：保持 Vue 专区合同接口路径和字段，避免侵入共享 service。 */
export const zoneContractApi = {
    async paging(params: SpecialZoneContractPagingParams): Promise<ZoneContractPageResult> {
        const { data } = await request.service.get<ZoneContractPageResult>("/admin/special-zone-contract/paging", {
            params
        });
        return data;
    },
    async count(): Promise<{ effectiveCount: number; cancelCount: number }> {
        const { data } = await request.service.get<{ effectiveCount: number; cancelCount: number }>(
            "/admin/special-zone-contract/get/contract/count"
        );
        return data;
    },
    /** 查询当前筛选条件下的物料汇总，沿用旧 Vue 列表的 find/count 接口。 */
    async summary(
        params: Omit<SpecialZoneContractPagingParams, "page" | "size">
    ): Promise<SpecialZoneContractPagingRow> {
        const { data } = await request.service.get<SpecialZoneContractPagingRow>(
            "/admin/special-zone-contract/find/count",
            { params }
        );
        return data;
    },
    async detail(id: number): Promise<SpecialZoneContractPagingRow> {
        const { data } = await request.service.get<SpecialZoneContractPagingRow>(
            `/admin/special-zone-contract/vo/${id}`
        );
        return data;
    },
    async create(params: SpecialZoneContractCreateParams) {
        const { data } = await request.service.post("/admin/special-zone-contract", params);
        return data;
    },
    async update(id: number, params: SpecialZoneContractUpdateParams) {
        const { data } = await request.service.put(`/admin/special-zone-contract/${id}`, params);
        return data;
    },
    /** 提交前复用旧 Vue 的合同编号查重规则，编辑当前合同本身不算重复。 */
    async hasDuplicateCode(contractCode: string, excludeId?: number): Promise<boolean> {
        const page = await this.paging({ page: 1, size: 50, contractCode });
        return (page.list || []).some(row => {
            const rowCode = row.specialZoneContract?.contractCode?.trim();
            return rowCode === contractCode && row.specialZoneContract?.id !== excludeId;
        });
    },
    async assign(id: number, handlerId: number) {
        await request.service.put(`/admin/special-zone-contract/assign/${id}/${handlerId}`);
    },
    async cancel(id: number) {
        await request.service.put(`/admin/special-zone-contract/cancel/${id}`);
    },
    async reservations(params: {
        contractId: number;
        page: number;
        size: number;
    }): Promise<ZoneContractReservationResult> {
        const { data } = await request.service.get<ZoneContractReservationResult>(
            "/admin/special-zone-reservation-order/list",
            { params }
        );
        return data;
    },
    async priceLogs(params: {
        contractId: number;
        page: number;
        size: number;
    }): Promise<{ list?: SpecialZoneContractPriceAdjustLog[]; total?: number }> {
        const { data } = await request.service.get<{ list?: SpecialZoneContractPriceAdjustLog[]; total?: number }>(
            "/admin/special-zone-contract/price-adjust-logs",
            { params }
        );
        return data;
    },
    async specials(): Promise<FindContractSpecialsItem[]> {
        const { data } = await request.service.get<FindContractSpecialsItem[]>(
            "/admin/special-zone-contract/find/specials"
        );
        // 专员接口返回的 user/personId 结构属于业务响应，保持原始 payload，避免丢失指派弹窗所需字段。
        return data;
    },
    async export(params: Omit<SpecialZoneContractPagingParams, "page" | "size">, filename: string) {
        const response = await request.service.get<Blob>("/admin/special-zone-contract/export", {
            params,
            responseType: "blob"
        });
        const url = window.URL.createObjectURL(response as unknown as Blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }
};
